import type { CompleteSSOLoginRequest } from '@compass/client/types/auth';
import { authService } from '@services/api';
import { useMutation } from '@tanstack/react-query';

import { sanitizeRedirectUrl } from '@/utils/urlValidation';

/**
 * SSO login redirects the whole document to the identity provider and back, which
 * wipes react-router `location.state` and the `?redirect=` query param. To return the
 * user to the page they were on, we persist the post-login target in sessionStorage
 * across the IdP round-trip.
 *
 * Only a sanitized, in-app route path is stored (never field values / PHI) — the same
 * kind of identifier already present in the browser URL.
 */
const SSO_REDIRECT_STORAGE_KEY = 'sso_redirect';

/** Persist the post-login redirect target before initiating an SSO round-trip. */
export const stashSsoRedirect = (path: string | null | undefined): void => {
  const safe = sanitizeRedirectUrl(path);
  // sanitizeRedirectUrl falls back to '/'; only stash a meaningful destination.
  if (safe !== '/') {
    sessionStorage.setItem(SSO_REDIRECT_STORAGE_KEY, safe);
  } else {
    // Clear any stale value so a later SSO login never inherits an old destination.
    sessionStorage.removeItem(SSO_REDIRECT_STORAGE_KEY);
  }
};

/** Read and clear the stashed SSO redirect target. Returns a sanitized path, or '/'. */
export const consumeSsoRedirect = (): string => {
  const stored = sessionStorage.getItem(SSO_REDIRECT_STORAGE_KEY);
  sessionStorage.removeItem(SSO_REDIRECT_STORAGE_KEY);
  return sanitizeRedirectUrl(stored);
};

export const useStartSsoLogin = () => {
  return useMutation({
    mutationFn: (organizationId: string) =>
      authService.startSsoLogin({ organization_id: organizationId }),
  });
};

export const useCompleteSsoLogin = () => {
  return useMutation({
    mutationFn: (
      params: Omit<CompleteSSOLoginRequest, 'connection_id'> & { connection_id: string },
    ) => authService.completeSsoLogin(params),
  });
};

/** Fire-and-forget mutation variant — used when you need to trigger a lookup imperatively. */
export const useGetSsoConnectionByDomain = () => {
  return useMutation({
    mutationFn: (domain: string) => authService.getSsoConnectionByDomain({ domain }),
  });
};

/** Start SSO login using a connection_id directly (from domain lookup). */
export const useStartSsoLoginByConnection = () => {
  return useMutation({
    mutationFn: (connectionId: string) =>
      authService.startSsoLoginByConnection({ connection_id: connectionId }),
  });
};
